import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from 'react-bootstrap/Card';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Alert from 'react-bootstrap/Alert';
import Spinner from 'react-bootstrap/Spinner';

import RepositoryUpload from '../components/RepositoryUpload';
import { useProjects } from '../contexts/ProjectContext';

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { createProject, uploadRepository, loading, error } = useProjects();

  const [projectName, setProjectName] = useState('');
  const [projectDomainDescription, setProjectDomainDescription] = useState('');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleCreateAndUpload = async (file: File, metadata?: { name?: string; description?: string }) => {
    if (!projectName.trim()) {
      setActionMessage({ type: 'danger', text: 'Provide a project name before uploading.' });
      throw new Error('Project name required');
    }
    setUploading(true);
    setActionMessage(null);
    try {
      const project = await createProject({ name: projectName.trim(), domain_description: projectDomainDescription.trim() || undefined });
      await uploadRepository(project.id, file, metadata);
      setActionMessage({ type: 'success', text: 'Repository uploaded and project created successfully.' });
      navigate(`/projects/${project.id}`);
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Upload failed';
      setActionMessage({ type: 'danger', text: message });
      throw err;
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mx-auto" style={{ maxWidth: 900 }}>
      <Row className="g-4 align-items-start">
        <Col md={5}>
          <Card className="shadow-sm">
            <Card.Body>
              <h4>Create Project</h4>
              <p className="text-muted">Name your project and optionally describe it. Each project can manage a single repository.</p>
              <Form>
                <Form.Group className="mb-3" controlId="projectName">
                  <Form.Label>Project Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="e.g. Customer Portal"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    disabled={uploading || loading}
                  />
                </Form.Group>
                <Form.Group className="mb-3" controlId="projectDomainDescription">
                  <Form.Label>Domain Description</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="Summarize the business domain, user personas, and responsibilities"
                    value={projectDomainDescription}
                    onChange={(e) => setProjectDomainDescription(e.target.value)}
                    disabled={uploading || loading}
                  />
                </Form.Group>
              </Form>
            </Card.Body>
          </Card>
        </Col>
        <Col md={7}>
          <RepositoryUpload
            title="Upload repository archive"
            description="The uploaded archive will be associated with the new project."
            onUpload={handleCreateAndUpload}
            uploading={uploading || loading}
          />
        </Col>
      </Row>

      {(error || actionMessage) && (
        <Alert variant={actionMessage?.type ?? 'danger'} className="mt-3">
          {actionMessage?.text || error}
        </Alert>
      )}

      {(uploading || loading) && (
        <div className="text-center mt-3"><Spinner animation="border" size="sm" /></div>
      )}
    </div>
  );
};

export default UploadPage;
